import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { opportunityAPI, clientAPI, accountAPI } from '../../utils/crmApi';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { ComboboxSearch } from '../../components/kibo-ui/combobox/';
import toast from 'react-hot-toast';
import {
    ArrowLeftIcon,
    CurrencyDollarIcon,
    CalendarIcon,
    DocumentTextIcon,
    BuildingOfficeIcon,
    UserIcon
} from '@heroicons/react/24/outline';

const OpportunityForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, currentAgency } = useCRM();
    const { branding } = useBranding();
    
    // Get client/account data from navigation state
    const contextData = location.state || {};
    
    const [formData, setFormData] = useState({
        name: '',
        value: '',
        stage: 'prospecting',
        probability: 10,
        closeDate: '',
        description: '',
        type: 'relocation',
        accountId: contextData.accountId || '',
        linkedClientId: contextData.clientId || ''
    });
    
    const [accounts, setAccounts] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    
    // Determine context (client or account) - reactive to state changes
    const { isFromClient, isFromAccount } = useMemo(() => {
        console.log('🔍 Context detection:', {
            contextData,
            isEditing,
            formDataLinkedClient: formData.linkedClientId,
            formDataAccount: formData.accountId
        });
        
        // If we have navigation context, use that
        if (contextData.clientId) {
            console.log('🔍 Using navigation context: client');
            return { isFromClient: true, isFromAccount: false };
        }
        if (contextData.accountId) {
            console.log('🔍 Using navigation context: account');
            return { isFromClient: false, isFromAccount: true };
        }
        
        // If editing and we have loaded opportunity data, determine from that
        if (isEditing && formData.linkedClientId && !formData.accountId) {
            console.log('🔍 Using edit context: client (has linkedClient, no account)');
            return { isFromClient: true, isFromAccount: false };
        }
        if (isEditing && formData.accountId) {
            console.log('🔍 Using edit context: account (has account)');
            return { isFromClient: false, isFromAccount: true };
        }
        
        // If editing but no clear context, try to determine from URL or other clues
        if (isEditing) {
            // Check if we came from a client detail page (this is a fallback)
            const referrer = document.referrer;
            if (referrer && referrer.includes('/clients/')) {
                console.log('🔍 Using referrer context: client');
                return { isFromClient: true, isFromAccount: false };
            }
            if (referrer && referrer.includes('/accounts/')) {
                console.log('🔍 Using referrer context: account');
                return { isFromClient: false, isFromAccount: true };
            }
        }
        
        // Default: neither (standalone)
        console.log('🔍 Using default context: standalone');
        return { isFromClient: false, isFromAccount: false };
    }, [contextData.clientId, contextData.accountId, isEditing, formData.linkedClientId, formData.accountId]);
    
    // Check if we're editing an existing opportunity
    useEffect(() => {
        const pathParts = location.pathname.split('/');
        console.log('🔍 URL path parts:', pathParts);
        
        // For URL like /crm/opportunities/123/edit, we want the ID (123)
        // For URL like /crm/opportunities/new, we want 'new'
        let opportunityId = null;
        
        if (pathParts.includes('opportunities')) {
            const opportunitiesIndex = pathParts.indexOf('opportunities');
            if (opportunitiesIndex + 1 < pathParts.length) {
                opportunityId = pathParts[opportunitiesIndex + 1];
            }
        }
        
        console.log('🔍 Extracted opportunity ID:', opportunityId);
        
        if (opportunityId && opportunityId !== 'new') {
            console.log('🔍 Setting isEditing to true and fetching opportunity:', opportunityId);
            setIsEditing(true);
            fetchOpportunity(opportunityId);
        }
    }, [location.pathname]);
    
    // Load accounts and clients on component mount
    useEffect(() => {
        fetchAccounts();
        fetchClients();
    }, []);
    
    const fetchOpportunity = async (id) => {
        try {
            console.log('🔍 Fetching opportunity with ID:', id);
            setLoading(true);
            const response = await opportunityAPI.getById(id);
            console.log('🔍 Opportunity API response:', response);
            const opportunity = response.data;
            console.log('🔍 Opportunity data:', opportunity);
            
            const newFormData = {
                name: opportunity.name || '',
                value: opportunity.value || '',
                stage: opportunity.stage || 'prospecting',
                probability: opportunity.probability || 10,
                closeDate: opportunity.closeDate ? new Date(opportunity.closeDate).toISOString().split('T')[0] : '',
                description: opportunity.description || '',
                type: opportunity.type || 'relocation',
                accountId: opportunity.accountId?._id || '',
                linkedClientId: opportunity.linkedClientId?._id || ''
            };
            
            console.log('🔍 Setting form data:', newFormData);
            setFormData(newFormData);
        } catch (error) {
            console.error('Error fetching opportunity:', error);
            console.log('🔍 Opportunity ID:', id);
            console.log('🔍 Error details:', error.response?.data || error.message);
            toast.error('Failed to load opportunity');
            // Don't navigate away immediately - let user decide what to do
            // navigate('/accounts');
        } finally {
            setLoading(false);
        }
    };
    
    const fetchAccounts = async () => {
        try {
            const response = await accountAPI.getAll();
            console.log('🔍 Accounts API response:', response);
            const accountsData = response.data || response || [];
            console.log('🔍 Accounts data:', accountsData);
            setAccounts(Array.isArray(accountsData) ? accountsData : []);
        } catch (error) {
            console.error('Error fetching accounts:', error);
            setAccounts([]);
        }
    };
    
    const fetchClients = async () => {
        try {
            const response = await clientAPI.getAll();
            console.log('🔍 Clients API response:', response);
            const clientsData = response.data || response || [];
            console.log('🔍 Clients data:', clientsData);
            setClients(Array.isArray(clientsData) ? clientsData : []);
        } catch (error) {
            console.error('Error fetching clients:', error);
            setClients([]);
        }
    };
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleSelectChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.value) {
            toast.error('Please fill in all required fields');
            return;
        }
        
        try {
            setLoading(true);
            
            // Filter out empty string values for ObjectId fields
            const filteredFormData = {
                ...formData,
                accountId: formData.accountId && formData.accountId.trim() !== '' ? formData.accountId : null,
                linkedClientId: formData.linkedClientId && formData.linkedClientId.trim() !== '' ? formData.linkedClientId : null
            };
            
            const opportunityData = {
                ...filteredFormData,
                value: parseFloat(filteredFormData.value),
                probability: parseInt(filteredFormData.probability),
                closeDate: filteredFormData.closeDate ? new Date(filteredFormData.closeDate) : null,
                agencyId: currentAgency._id,
                createdBy: user._id,
                updatedBy: user._id
            };
            
            console.log('🔍 Opportunity data being sent:', opportunityData);
            
            if (isEditing) {
                // Extract opportunity ID using the same logic as in useEffect
                const pathParts = location.pathname.split('/');
                let opportunityId = null;
                
                if (pathParts.includes('opportunities')) {
                    const opportunitiesIndex = pathParts.indexOf('opportunities');
                    if (opportunitiesIndex + 1 < pathParts.length) {
                        opportunityId = pathParts[opportunitiesIndex + 1];
                    }
                }
                
                console.log('🔍 Save: Extracted opportunity ID:', opportunityId);
                
                if (!opportunityId) {
                    toast.error('Unable to determine opportunity ID');
                    return;
                }
                
                await opportunityAPI.update(opportunityId, opportunityData);
                toast.success('Opportunity updated successfully');
            } else {
                await opportunityAPI.create(opportunityData);
                toast.success('Opportunity created successfully');
            }
            
            // Navigate back to the appropriate page with opportunities tab
            console.log('🔍 Navigation logic:', {
                isFromClient,
                isFromAccount,
                contextData,
                formDataLinkedClient: formData.linkedClientId,
                formDataAccount: formData.accountId
            });
            
            if (isFromClient) {
                const clientId = contextData.clientId || formData.linkedClientId;
                console.log('🔍 Navigating to client:', clientId);
                navigate(`/clients/${clientId}?tab=opportunities`);
            } else if (isFromAccount) {
                const accountId = contextData.accountId || formData.accountId;
                console.log('🔍 Navigating to account:', accountId);
                navigate(`/accounts/${accountId}?tab=opportunities`);
            } else {
                console.log('🔍 Navigating to accounts list (fallback)');
                navigate('/accounts');
            }
        } catch (error) {
            console.error('Error saving opportunity:', error);
            toast.error('Failed to save opportunity');
        } finally {
            setLoading(false);
        }
    };
    
    const handleCancel = () => {
        console.log('🔍 Cancel navigation logic:', {
            isFromClient,
            isFromAccount,
            contextData,
            formDataLinkedClient: formData.linkedClientId,
            formDataAccount: formData.accountId
        });
        
        if (isFromClient) {
            const clientId = contextData.clientId || formData.linkedClientId;
            console.log('🔍 Cancel navigating to client:', clientId);
            navigate(`/clients/${clientId}?tab=opportunities`);
        } else if (isFromAccount) {
            const accountId = contextData.accountId || formData.accountId;
            console.log('🔍 Cancel navigating to account:', accountId);
            navigate(`/accounts/${accountId}?tab=opportunities`);
        } else {
            console.log('🔍 Cancel navigating to accounts list (fallback)');
            navigate('/accounts');
        }
    };
    
    if (loading && isEditing) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={handleCancel}
                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
                    >
                        <ArrowLeftIcon className="h-5 w-5" />
                        Back
                    </button>
                    
                    <div className="flex items-center gap-3">
                        <div 
                            className="p-3 rounded-lg"
                            style={{ backgroundColor: `${branding.primaryColor}20` }}
                        >
                            <CurrencyDollarIcon 
                                className="h-8 w-8"
                                style={{ color: branding.primaryColor }}
                            />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-foreground">
                                {isEditing 
                                    ? 'Edit Opportunity' 
                                    : isFromClient 
                                        ? `Create Opportunity for ${contextData.clientName || 'Client'}`
                                        : isFromAccount 
                                            ? `Create Opportunity for ${contextData.accountName || 'Account'}`
                                            : 'Create Opportunity'
                                }
                            </h1>
                            <p className="text-muted-foreground">
                                {isEditing 
                                    ? 'Update opportunity details' 
                                    : isFromClient 
                                        ? 'Add a new opportunity for this client'
                                        : isFromAccount 
                                            ? 'Add a new opportunity for this account'
                                            : 'Add a new opportunity to track potential revenue'
                                }
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Form */}
                <div className="bg-card rounded-lg shadow-sm border border-border">
                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Opportunity Name */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Opportunity Name *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    placeholder="e.g., Relocation Services for John Smith"
                                    required
                                />
                            </div>
                            
                            {/* Value */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Value ($) *
                                </label>
                                <input
                                    type="number"
                                    name="value"
                                    value={formData.value}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    placeholder="50000"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </div>
                            
                            {/* Stage */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Stage
                                </label>
                                <select
                                    name="stage"
                                    value={formData.stage}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                >
                                    <option value="prospecting">Prospecting</option>
                                    <option value="qualification">Qualification</option>
                                    <option value="needs_analysis">Needs Analysis</option>
                                    <option value="value_proposition">Value Proposition</option>
                                    <option value="decision_makers">Decision Makers</option>
                                    <option value="perception_analysis">Perception Analysis</option>
                                    <option value="proposal_sent">Proposal Sent</option>
                                    <option value="negotiation">Negotiation</option>
                                    <option value="closed_won">Closed Won</option>
                                    <option value="closed_lost">Closed Lost</option>
                                </select>
                            </div>
                            
                            {/* Probability */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Probability (%)
                                </label>
                                <input
                                    type="number"
                                    name="probability"
                                    value={formData.probability}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    min="0"
                                    max="100"
                                />
                            </div>
                            
                            {/* Close Date */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Close Date
                                </label>
                                <input
                                    type="date"
                                    name="closeDate"
                                    value={formData.closeDate}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                />
                            </div>
                            
                            {/* Type */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Type
                                </label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                >
                                    <option value="relocation">Relocation</option>
                                    <option value="visa">Visa</option>
                                    <option value="property">Property</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            
                            {/* Account - only show when creating (not editing) and not from client context */}
                            {!isEditing && !isFromClient && (
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Account
                                    </label>
                                    <ComboboxSearch
                                        data={Array.isArray(accounts) ? accounts : []}
                                        value={formData.accountId}
                                        onValueChange={(value) => handleSelectChange('accountId', value)}
                                        placeholder="Search and select account..."
                                        displayKey="name"
                                        valueKey="_id"
                                        type="button"
                                    />
                                </div>
                            )}
                            
                            {/* Linked Client - only show when creating (not editing) and not from client context */}
                            {!isEditing && !isFromClient && (
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Link to Client (Optional)
                                    </label>
                                    <ComboboxSearch
                                        data={Array.isArray(clients) ? clients : []}
                                        value={formData.linkedClientId}
                                        onValueChange={(value) => handleSelectChange('linkedClientId', value)}
                                        placeholder="Search and select client..."
                                        displayKey="name"
                                        valueKey="_id"
                                        type="button"
                                    />
                                </div>
                            )}
                        </div>
                        
                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={4}
                                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                placeholder="Additional details about this opportunity..."
                            />
                        </div>
                        
                        {/* Form Actions */}
                        <div className="flex items-center justify-end gap-4 pt-6 border-t border-border">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="px-6 py-2 text-foreground bg-background border border-border rounded-lg hover:bg-muted transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                style={{ backgroundColor: branding.primaryColor }}
                            >
                                {loading ? 'Saving...' : (isEditing ? 'Update Opportunity' : 'Create Opportunity')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default OpportunityForm;
